import Dexie from 'dexie';
import type { CompanyEntity } from 'shared/domain/company/types/entity';
import type { HttpRequestEntity } from 'shared/domain/httpRequest/httpRequestModel';
import type { OrganizationEntity } from 'shared/domain/organization/types/entity';
import type { ProjectEntity } from 'shared/domain/project/types/entity';
import type { SiteEntity } from 'shared/domain/site/types/entity';
import type { DirectoryEntity } from 'serviceWorker/repository/directory/entity';
import type { DocumentEntity } from 'serviceWorker/repository/document/entity';
import type { DocumentationEntity } from 'serviceWorker/repository/documentation/entity';
import type { InspectionEntity } from 'serviceWorker/repository/inspection/entity';
import type { InspectionTemplateEntity } from 'serviceWorker/repository/inspectionTemplate/entity';
import type { IssueEntity } from 'serviceWorker/repository/issue/entity';
import type { LevelEntity } from 'shared/domain/level/types/entity';
import type { UserEntity } from 'shared/domain/user/types/entity';
import type { VisibleFieldEntity } from 'serviceWorker/repository/visibleField/visibleFieldEntity';
import type { Identificable } from 'shared/types/commonView';
import type { ContractEntity } from 'serviceWorker/repository/contract/entity';
import type { ChartDataEntity } from './analytics';
import type { ChartFiltersEntity } from './chartFilters';
import type { CompaniesServiceData } from './companies/companiesService';
import type { ConfigData } from './config';
import type { ContractsServiceData } from './contracts/contractsService';
import type { CorrectiveActionTypesServiceData } from './fieldValues/correctiveActionTypes/correctiveActionTypesService';
import type { DirectoriesServiceData } from './directories/directoriesService';
import type { DocumentationsServiceData } from './documentations/documentationsService';
import type { EnvironmentalAspectsServiceData } from './fieldValues/environmentalAspects/environmentalAspectsService';
import type { HazardCategoriesServiceData } from './fieldValues/hazardCategories/hazardCategoriesService';
import type { InspectionTemplatesServiceData } from './inspectionTemplates/inspectionTemplatesService';
import type { InspectionsServiceData } from './inspections/inspectionsService';
import type { IssueFormServiceData } from './issueForm/issueFormService';
import type { IssueWorkflowsServiceData } from './issueWorkflows/issueWorkflowsService';
import type { IssuesServiceData } from './issues/issuesService';
import type { LevelsServiceData } from './levels/levelsService';
import {
  version15Store,
  version15Upgrade,
  version18Store,
  version18Upgrade,
  version23Store,
  version23Upgrade,
  version28Store,
  version28Upgrade,
  version29Upgrade,
  version30Upgrade,
  version31Store,
  version32Store,
  version32Upgrade,
  version33Upgrade,
  version34Store,
  version35Upgrade,
  version38Upgrade,
  version39Store,
  version41Store,
  version42Store,
  version43Store,
  version44Store,
  version45Store,
  version46Store,
  version46Upgrade,
  version47Upgrade,
  version50Store,
  version51Store,
} from './migrations';
import type { OrganizationsServiceData } from './organizations/organizationsService';
import type { PermissionsData } from './permissions';
import type { ProjectsServiceData } from './projects/projectsService';
import type { SitesServiceData } from './sites/sitesService';
import type { UsersServiceData } from './users/usersService';
import type { VisibleFieldsServiceData } from './visibleFields/visibleFieldsService';
import type { WorktypesServiceData } from './fieldValues/worktypes/worktypesService';
import type { DelayReasonsServiceData } from './fieldValues/delayReasons/delayReasonsService';
import type { IssueVerificationItemsServiceData } from './fieldValues/issueVerificationItems/issueVerificationItemsService';
import type { RejectionReasonsServiceData } from './fieldValues/rejectionReasons/rejectionReasonsService';
import type { RootCausesServiceData } from './fieldValues/rootCauses/rootCausesService';
import type { EffectsServiceData } from './fieldValues/effects/effectsService';
import type { CircumstancesServiceData } from './fieldValues/circumstances/circumstancesService';
import type { ImpactsServiceData } from './fieldValues/impacts/impactsService';
import type { DecisionsToImposeFineServiceData } from './fieldValues/decisionsToImposeFine/decisionsToImposeFineService';
import type { SpilledSubstancesServiceData } from './fieldValues/spilledSubstances/spilledSubstancesService';
import type { PrioritiesServiceData } from './fieldValues/priorities/prioritiesService';
import type { ProjectStagesServiceData } from './fieldValues/projectStages/projectStagesService';
import type { EmploymentTypesServiceData } from './fieldValues/employmentTypes/employmentTypesService';
import type { InjuryTypesServiceData } from './fieldValues/injuryTypes/injuryTypesService';
import type { InjuredBodyPartsServiceData } from './fieldValues/injuredBodyParts/injuredBodyPartsService';
import type { RequirementsServiceData } from './fieldValues/requirements/requirementsService';
import type { InstallationBasisServiceData } from './fieldValues/installationBasis/installationBasisService';
import type { ConstructionProductDocumentsServiceData } from './fieldValues/constructionProductDocuments/constructionProductDocumentsService';
import type { DeviceDocumentsServiceData } from './fieldValues/deviceDocuments/deviceDocumentsService';
import type { AdditionalDocumentsServiceData } from './fieldValues/additionalDocuments/additionalDocumentsService';
import type { FieldValueEntity } from 'shared/domain/fieldValue/entity';

type HustroTables = Record<HustroDbTablesUnion, Dexie.Table<unknown>>;

export type HustroDbTablesUnion =
  | HustroDbEntityTablesUnion
  | HustroDbServiceTablesUnion
  | 'config'
  | 'permissions'
  | 'analytics'
  | 'chartFilters'
  | 'selectedProject'
  | 'httpRequests'
  | 'documents';

export type HustroDbServiceTablesUnion =
  | 'issuesService'
  | 'companiesService'
  | 'contractsService'
  | 'usersService'
  | 'projectsService'
  | 'organizationsService'
  | 'inspectionTemplatesService'
  | 'inspectionsService'
  | 'sitesService'
  | 'worktypesService'
  | 'hazardCategoriesService'
  | 'environmentalAspectsService'
  | 'correctiveActionTypesService'
  | 'rejectionReasonsService'
  | 'delayReasonsService'
  | 'issueVerificationItemsService'
  | 'rootCausesService'
  | 'effectsService'
  | 'circumstancesService'
  | 'impactsService'
  | 'decisionsToImposeFineService'
  | 'spilledSubstancesService'
  | 'prioritiesService'
  | 'projectStagesService'
  | 'employmentTypesService'
  | 'injuryTypesService'
  | 'injuredBodyPartsService'
  | 'requirementsService'
  | 'installationBasisService'
  | 'constructionProductDocumentsService'
  | 'deviceDocumentsService'
  | 'additionalDocumentsService'
  | 'levelsService'
  | 'documentationsService'
  | 'directoriesService'
  | 'issueFormService'
  | 'issueWorkflowsService'
  | 'visibleFieldsService';

export type HustroDbEntityTablesUnion =
  | 'issues'
  | 'companies'
  | 'contracts'
  | 'users'
  | 'projects'
  | 'organizations'
  | 'inspectionTemplates'
  | 'inspections'
  | 'sites'
  | 'worktypes'
  | 'hazardCategories'
  | 'environmentalAspects'
  | 'correctiveActionTypes'
  | 'rejectionReasons'
  | 'delayReasons'
  | 'issueVerificationItems'
  | 'rootCauses'
  | 'effects'
  | 'circumstances'
  | 'impacts'
  | 'decisionsToImposeFine'
  | 'spilledSubstances'
  | 'priorities'
  | 'projectStages'
  | 'employmentTypes'
  | 'injuryTypes'
  | 'injuredBodyParts'
  | 'requirements'
  | 'installationBasis'
  | 'constructionProductDocuments'
  | 'deviceDocuments'
  | 'additionalDocuments'
  | 'levels'
  | 'documentations'
  | 'directories'
  | 'issueForm'
  | 'issueWorkflows'
  | 'visibleFields';

export class HustroDb extends Dexie implements HustroTables {
  analytics!: Dexie.Table<ChartDataEntity>;
  chartFilters!: Dexie.Table<ChartFiltersEntity>;
  companies!: Dexie.Table<CompanyEntity>;
  companiesService!: Dexie.Table<CompaniesServiceData>;
  config!: Dexie.Table<ConfigData, number, Omit<ConfigData, '_id'>>;
  contracts!: Dexie.Table<ContractEntity>;
  contractsService!: Dexie.Table<ContractsServiceData>;
  documents!: Dexie.Table<DocumentEntity>;
  hazardCategories!: Dexie.Table<FieldValueEntity>;
  hazardCategoriesService!: Dexie.Table<HazardCategoriesServiceData>;
  httpRequests!: Dexie.Table<HttpRequestEntity>;
  inspections!: Dexie.Table<InspectionEntity>;
  inspectionsService!: Dexie.Table<InspectionsServiceData>;
  inspectionTemplates!: Dexie.Table<InspectionTemplateEntity>;
  inspectionTemplatesService!: Dexie.Table<InspectionTemplatesServiceData>;
  issues!: Dexie.Table<IssueEntity>;
  issuesService!: Dexie.Table<IssuesServiceData>;
  permissions!: Dexie.Table<PermissionsData>;
  projects!: Dexie.Table<ProjectEntity>;
  projectsService!: Dexie.Table<ProjectsServiceData>;
  organizations!: Dexie.Table<OrganizationEntity>;
  organizationsService!: Dexie.Table<OrganizationsServiceData>;
  selectedProject!: Dexie.Table<Identificable>;
  sites!: Dexie.Table<SiteEntity>;
  sitesService!: Dexie.Table<SitesServiceData>;
  users!: Dexie.Table<UserEntity>;
  usersService!: Dexie.Table<UsersServiceData>;
  worktypes!: Dexie.Table<FieldValueEntity>;
  worktypesService!: Dexie.Table<WorktypesServiceData>;
  environmentalAspects!: Dexie.Table<FieldValueEntity>;
  environmentalAspectsService!: Dexie.Table<EnvironmentalAspectsServiceData>;
  rejectionReasons!: Dexie.Table<FieldValueEntity>;
  rejectionReasonsService!: Dexie.Table<RejectionReasonsServiceData>;
  delayReasons!: Dexie.Table<FieldValueEntity>;
  delayReasonsService!: Dexie.Table<DelayReasonsServiceData>;
  issueVerificationItems!: Dexie.Table<FieldValueEntity>;
  issueVerificationItemsService!: Dexie.Table<IssueVerificationItemsServiceData>;
  rootCauses!: Dexie.Table<FieldValueEntity>;
  rootCausesService!: Dexie.Table<RootCausesServiceData>;
  effects!: Dexie.Table<FieldValueEntity>;
  effectsService!: Dexie.Table<EffectsServiceData>;
  circumstances!: Dexie.Table<FieldValueEntity>;
  circumstancesService!: Dexie.Table<CircumstancesServiceData>;
  impacts!: Dexie.Table<FieldValueEntity>;
  impactsService!: Dexie.Table<ImpactsServiceData>;
  decisionsToImposeFine!: Dexie.Table<FieldValueEntity>;
  decisionsToImposeFineService!: Dexie.Table<DecisionsToImposeFineServiceData>;
  spilledSubstances!: Dexie.Table<FieldValueEntity>;
  spilledSubstancesService!: Dexie.Table<SpilledSubstancesServiceData>;
  priorities!: Dexie.Table<FieldValueEntity>;
  prioritiesService!: Dexie.Table<PrioritiesServiceData>;
  projectStages!: Dexie.Table<FieldValueEntity>;
  projectStagesService!: Dexie.Table<ProjectStagesServiceData>;
  employmentTypes!: Dexie.Table<FieldValueEntity>;
  employmentTypesService!: Dexie.Table<EmploymentTypesServiceData>;
  injuryTypes!: Dexie.Table<FieldValueEntity>;
  injuryTypesService!: Dexie.Table<InjuryTypesServiceData>;
  injuredBodyParts!: Dexie.Table<FieldValueEntity>;
  injuredBodyPartsService!: Dexie.Table<InjuredBodyPartsServiceData>;
  requirements!: Dexie.Table<FieldValueEntity>;
  requirementsService!: Dexie.Table<RequirementsServiceData>;
  installationBasis!: Dexie.Table<FieldValueEntity>;
  installationBasisService!: Dexie.Table<InstallationBasisServiceData>;
  constructionProductDocuments!: Dexie.Table<FieldValueEntity>;
  constructionProductDocumentsService!: Dexie.Table<ConstructionProductDocumentsServiceData>;
  deviceDocuments!: Dexie.Table<FieldValueEntity>;
  deviceDocumentsService!: Dexie.Table<DeviceDocumentsServiceData>;
  additionalDocuments!: Dexie.Table<FieldValueEntity>;
  additionalDocumentsService!: Dexie.Table<AdditionalDocumentsServiceData>;
  correctiveActionTypes!: Dexie.Table<FieldValueEntity>;
  correctiveActionTypesService!: Dexie.Table<CorrectiveActionTypesServiceData>;
  levels!: Dexie.Table<LevelEntity>;
  levelsService!: Dexie.Table<LevelsServiceData>;
  documentations!: Dexie.Table<DocumentationEntity>;
  documentationsService!: Dexie.Table<DocumentationsServiceData>;
  directories!: Dexie.Table<DirectoryEntity>;
  directoriesService!: Dexie.Table<DirectoriesServiceData>;
  issueForm!: Dexie.Table<any>;
  issueFormService!: Dexie.Table<IssueFormServiceData>;
  issueWorkflows!: Dexie.Table<any>;
  issueWorkflowsService!: Dexie.Table<IssueWorkflowsServiceData>;
  visibleFields!: Dexie.Table<VisibleFieldEntity>;
  visibleFieldsService!: Dexie.Table<VisibleFieldsServiceData>;

  constructor() {
    super('hustro');

    // https://dexie.org/docs/Tutorial/Design#database-versioning
    this.version(15).stores(version15Store).upgrade(version15Upgrade);

    this.version(18).stores(version18Store).upgrade(version18Upgrade);

    this.version(23).stores(version23Store).upgrade(version23Upgrade);

    this.version(28).stores(version28Store).upgrade(version28Upgrade);

    this.version(29).upgrade(version29Upgrade);

    this.version(30).upgrade(version30Upgrade);

    this.version(31).stores(version31Store);

    this.version(32).stores(version32Store).upgrade(version32Upgrade);

    this.version(33).stores({}).upgrade(version33Upgrade);

    this.version(34).stores(version34Store);

    this.version(35).stores({}).upgrade(version35Upgrade);

    this.version(36).stores({
      visibleFields: '&_id, deleted',
      visibleFieldsService: '++_id',
    });

    this.version(37).stores({
      issueForm: '&name',
      issueFormService: '++_id',
      // https://github.com/dexie/Dexie.js/issues/563
      issueForms: null,
      issueFormsService: null,
    });

    this.version(38).stores({}).upgrade(version38Upgrade);

    this.version(39).stores(version39Store);

    this.version(41).stores(version41Store);

    this.version(42).stores(version42Store);

    this.version(43).stores(version43Store);

    this.version(44).stores(version44Store);

    this.version(45).stores(version45Store);

    this.version(46).stores(version46Store).upgrade(version46Upgrade);

    this.version(47).stores({}).upgrade(version47Upgrade);

    this.version(50).stores(version50Store);

    this.version(51).stores(version51Store);

    this.open();
  }
}

const db = new HustroDb();

export default db;
